import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import SettingsIcon from "@material-ui/icons/Settings";
import PeopleIcon from "@material-ui/icons/People";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import Chip from "@material-ui/core/Chip";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    // padding: theme.spacing(1),
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  
  // ✅ ESTADOS PARA MODAL DE CONFIGURAÇÕES
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [settings, setSettings] = useState({
    messageInterval: 60,
    longerIntervalAfter: 20,
    greaterInterval: 120,
  });
  // ✅ ESTADOS PARA MODAL DE VARIÁVEIS GLOBAIS (POR EMPRESA)
  const [variablesModalOpen, setVariablesModalOpen] = useState(false);
  const [companyVars, setCompanyVars] = useState({}); // { chave: [op1, op2] }
  const [varKey, setVarKey] = useState("");
  const [varOption, setVarOption] = useState("");
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    };

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  // ✅ FUNÇÕES PARA MODAL DE CONFIGURAÇÕES
  const handleOpenConfigModal = () => {
    setConfigModalOpen(true);
    loadCampaignSettings();
  };

  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
  };

  // ✅ FUNÇÕES PARA MODAL DE VARIÁVEIS
  const handleOpenVariablesModal = async () => {
    setVariablesModalOpen(true);
    await loadCompanyVariables();
  };

  const handleCloseVariablesModal = () => {
    setVariablesModalOpen(false);
    setVarKey("");
    setVarOption("");
  };

  const handleOpenContactLists = () => {
    history.push('/contact-lists');
  };

  const loadCampaignSettings = async () => {
    try {
      const { data } = await api.get("/campaign-settings");
      const settingsList = [];
      if (Array.isArray(data) && data.length > 0) {
        data.forEach((item) => {
          settingsList.push([item.key, item.value]);
        });
        setSettings(Object.fromEntries(settingsList));
      }
    } catch (err) {
      toastError(err);
    }
  };

  const loadCompanyVariables = async () => {
    try {
      const { data } = await api.get("/campaign-settings");
      let obj = {};
      if (Array.isArray(data)) {
        const item = data.find((s) => s.key === "textVariables");
        if (item && item.value) {
          try {
            obj = typeof item.value === "string" ? JSON.parse(item.value) : item.value;
          } catch (e) {
            obj = {};
          }
        }
      }
      setCompanyVars(obj || {});
    } catch (err) {
      toastError(err);
    }
  };

  const handleOnChangeSettings = (e) => {
    const changedProp = {};
    changedProp[e.target.name] = e.target.value;
    setSettings((prev) => ({ ...prev, ...changedProp }));
  };

  const saveSettings = async () => {
    try {
      await api.post("/campaign-settings", { settings });
      toast.success("Configurações salvas com sucesso!");
      handleCloseConfigModal();
    } catch (err) {
      toastError(err);
    }
  };

  // ✅ HELPERS PARA VARIÁVEIS
  const normalizedKey = (key) => (key || "").toString().trim().toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 30);
  const addVarOption = () => {
    const k = normalizedKey(varKey);
    const opt = (varOption || "").toString().trim();
    if (!k || !opt) return;
    setCompanyVars((prev) => {
      const current = Array.isArray(prev[k]) ? prev[k] : [];
      // Limites
      if (current.length >= 20) return prev; // até 20 opções por variável
      if (Object.keys(prev).length >= 10 && !prev[k]) return prev; // até 10 variáveis
      if (current.includes(opt)) return prev; // sem duplicatas
      return { ...prev, [k]: [...current, opt] };
    });
    setVarOption("");
    if (!varKey) setVarKey(k);
  };
  const removeVarOption = (k, opt) => {
    setCompanyVars((prev) => {
      const current = Array.isArray(prev[k]) ? prev[k] : [];
      const next = current.filter((o) => o !== opt);
      const clone = { ...prev };
      if (next.length === 0) delete clone[k]; else clone[k] = next;
      return clone;
    });
  };
  const removeVariable = (k) => {
    setCompanyVars((prev) => {
      const clone = { ...prev };
      delete clone[k];
      return clone;
    });
  };
  const saveCompanyVariables = async () => {
    try {
      await api.post("/campaign-settings", { settings: { textVariables: companyVars } });
      toast.success("Variáveis salvas com sucesso!");
      handleCloseVariablesModal();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return "Inativa";
      case "PROGRAMADA":
        return "Programada";
      case "EM_ANDAMENTO":
        return "Em Andamento";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${
            deletingCampaign.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}

      {/* ✅ MODAL DE CONFIGURAÇÕES */}
      <Dialog
        open={configModalOpen}
        onClose={handleCloseConfigModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 12
          }
        }}
      >
        <DialogTitle style={{ textAlign: 'center', color: '#1976d2', fontWeight: 'bold' }}>
          ⚙️ Configurações de Campanhas
        </DialogTitle>
        
        <DialogContent dividers style={{ padding: 24 }}>
          <Typography variant="h6" style={{ marginBottom: 20, color: '#1976d2' }}>
            Intervalos de Envio
          </Typography>
          
          <Grid spacing={3} container>
            <Grid xs={12} md={4} item>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="messageInterval-label">
                  Intervalo Randômico
                </InputLabel>
                <Select
                  name="messageInterval"
                  id="messageInterval"
                  labelId="messageInterval-label"
                  label="Intervalo Randômico"
                  value={settings.messageInterval || 60}
                  onChange={handleOnChangeSettings}
                >
                  <MenuItem value={60}>60 segundos (1 minuto)</MenuItem>
                  <MenuItem value={120}>120 segundos (2 minutos)</MenuItem>
                  <MenuItem value={180}>180 segundos (3 minutos)</MenuItem>
                  <MenuItem value={300}>300 segundos (5 minutos)</MenuItem>
                  <MenuItem value={600}>600 segundos (10 minutos)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} md={4} item>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="longerIntervalAfter-label">
                  Intervalo Maior Após
                </InputLabel>
                <Select
                  name="longerIntervalAfter"
                  id="longerIntervalAfter"
                  labelId="longerIntervalAfter-label"
                  label="Intervalo Maior Após"
                  value={settings.longerIntervalAfter || 20}
                  onChange={handleOnChangeSettings}
                >
                  <MenuItem value={0}>Indefinido</MenuItem>
                  <MenuItem value={5}>5 mensagens</MenuItem>
                  <MenuItem value={10}>10 mensagens</MenuItem>
                  <MenuItem value={15}>15 mensagens</MenuItem>
                  <MenuItem value={20}>20 mensagens</MenuItem>
                  <MenuItem value={25}>25 mensagens</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} md={4} item>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="greaterInterval-label">
                  Intervalo Maior
                </InputLabel>
                <Select
                  name="greaterInterval"
                  id="greaterInterval"
                  labelId="greaterInterval-label"
                  label="Intervalo Maior"
                  value={settings.greaterInterval || 120}
                  onChange={handleOnChangeSettings}
                >
                  <MenuItem value={120}>2 minutos</MenuItem>
                  <MenuItem value={300}>5 minutos</MenuItem>
                  <MenuItem value={600}>10 minutos</MenuItem>
                  <MenuItem value={900}>15 minutos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box style={{ 
            marginTop: 24, 
            padding: 16, 
            backgroundColor: '#e3f2fd', 
            borderRadius: 8,
            border: '1px solid #2196f3'
          }}>
            <Typography variant="body2" style={{ color: '#1565c0', fontWeight: 'bold' }}>
              💡 Dica: Configurações mais conservadoras reduzem o risco de bloqueio pelo WhatsApp
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
          <Button
            onClick={handleCloseConfigModal}
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={saveSettings}
            color="primary"
            variant="contained"
            style={{ borderRadius: '22px' }}
          >
            Salvar Configurações
          </Button>
        </DialogActions>
      </Dialog>
      
      {
        user.profile === "user"?
          <ForbiddenPage />
          :
          <>
            <MainHeader>
              <Grid style={{ width: "99.6%" }} container>
                <Grid xs={12} sm={8} item>
                  <Title>{i18n.t("campaigns.title")}</Title>
                </Grid>
                <Grid xs={12} sm={12} item>
                  <Grid spacing={1} container justifyContent="flex-start" alignItems="center">
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={handleOpenConfigModal}
                        startIcon={<SettingsIcon />}
                        style={{
                          minHeight: '40px',
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          padding: '8px 16px',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: theme.palette.primary.main + '08'
                          }
                        }}
                      >
                        CONFIGURAÇÃO
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={handleOpenVariablesModal}
                        style={{
                          minHeight: '40px',
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          padding: '8px 16px',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: theme.palette.primary.main + '08'
                          }
                        }}
                      >
                        VARIÁVEIS
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={handleOpenContactLists}
                        startIcon={<PeopleIcon />}
                        style={{
                          minHeight: '40px',
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          padding: '8px 16px',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: theme.palette.primary.main + '08'
                          }
                        }}
                      >
                        LISTA DE CONTATOS
                      </Button>
                    </Grid>
                    <Grid item style={{ flex: 1, maxWidth: '300px', marginLeft: '16px', marginRight: '16px' }}>
                      <TextField
                        fullWidth
                        placeholder="Pesquisar..."
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        variant="outlined"
                        style={{
                          backgroundColor: '#f5f5f5'
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon style={{ color: "#1976d2" }} />
                            </InputAdornment>
                          ),
                          style: {
                            height: '40px',
                            fontSize: '0.875rem',
                            borderRadius: '8px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        onClick={handleOpenCampaignModal}
                        color="primary"
                        style={{
                          minHeight: '40px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          whiteSpace: 'nowrap',
                          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                          boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)'
                        }}
                      >
                        NOVA CAMPANHA
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </MainHeader>
            <Paper
              className={classes.mainPaper}
              variant="outlined"
              onScroll={handleScroll}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.name")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.status")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.contactList")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.whatsapp")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.scheduledAt")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.completedAt")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.confirmation")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell align="center">{campaign.name}</TableCell>
                        <TableCell align="center">
                          {formatStatus(campaign.status)}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.contactListId
                            ? campaign.contactList.name
                            : "Não definida"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.whatsappId
                            ? campaign.whatsapp.name
                            : "Não definido"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.scheduledAt
                            ? datetimeToClient(campaign.scheduledAt)
                            : "Sem agendamento"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.completedAt
                            ? datetimeToClient(campaign.completedAt)
                            : "Não concluída"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.confirmation ? "Habilitada" : "Desabilitada"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.status === "EM_ANDAMENTO" && (
                            <IconButton
                              onClick={() => cancelCampaign(campaign)}
                              title="Parar Campanha"
                              size="small"
                            >
                              <PauseCircleOutlineIcon />
                            </IconButton>
                          )}
                          {campaign.status === "CANCELADA" && (
                            <IconButton
                              onClick={() => restartCampaign(campaign)}
                              title="Parar Campanha"
                              size="small"
                            >
                              <PlayCircleOutlineIcon />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() =>
                              history.push(`/campaign/${campaign.id}/report`)
                            }
                            size="small"
                          >
                            <DescriptionIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditCampaign(campaign)}
                          >
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setConfirmModalOpen(true);
                              setDeletingCampaign(campaign);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {loading && <TableRowSkeleton columns={8} />}
                  </>
                </TableBody>
              </Table>
            </Paper>
            {/* ✅ MODAL DE VARIÁVEIS DA EMPRESA */}
            <Dialog
              open={variablesModalOpen}
              onClose={handleCloseVariablesModal}
              maxWidth="md"
              fullWidth
              PaperProps={{ style: { borderRadius: 12 } }}
            >
              <DialogTitle style={{ textAlign: 'center', color: '#1976d2', fontWeight: 'bold' }}>
                🧩 Variáveis da Empresa
              </DialogTitle>
              <DialogContent dividers style={{ padding: 24 }}>
                <Typography variant="body2" style={{ marginBottom: 16, color: '#555' }}>
                  Crie variáveis reutilizáveis para personalizar campanhas. Ex.: saudacao → Olá, Oi, Hey. Uso: {"{saudacao}"}.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Nome da variável"
                      value={varKey}
                      onChange={(e) => setVarKey(e.target.value)}
                      placeholder="ex.: saudacao"
                      variant="outlined"
                      fullWidth
                      size="small"
                      helperText="Use letras/números, _ ou - (máx. 30)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Adicionar opção"
                      value={varOption}
                      onChange={(e) => setVarOption(e.target.value)}
                      placeholder="ex.: Olá"
                      variant="outlined"
                      fullWidth
                      size="small"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVarOption(); } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2} style={{ display: 'flex', alignItems: 'center' }}>
                    <Button variant="contained" color="primary" onClick={addVarOption}>
                      Adicionar
                    </Button>
                  </Grid>
                </Grid>
                <Box style={{ marginTop: 16 }}>
                  {Object.keys(companyVars).length === 0 && (
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Nenhuma variável criada ainda.
                    </Typography>
                  )}
                  {Object.entries(companyVars).map(([k, opts]) => (
                    <Box key={k} style={{ marginBottom: 12 }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>{`{${k}}`}</Typography>
                        <Button size="small" color="secondary" onClick={() => removeVariable(k)}>Remover</Button>
                      </Box>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {Array.isArray(opts) && opts.map((opt) => (
                          <Chip key={`${k}-${opt}`} label={opt} onDelete={() => removeVarOption(k, opt)} />
                        ))}
                      </div>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" style={{ display: 'block', marginTop: 12, color: '#555' }}>
                  Prioridade: inline {"{chave:op1|op2}"} &gt; variáveis da campanha &gt; variáveis da empresa &gt; dados do contato.
                </Typography>
              </DialogContent>
              <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
                <Button onClick={handleCloseVariablesModal} color="default" variant="outlined">
                  Cancelar
                </Button>
                <Button onClick={saveCompanyVariables} color="primary" variant="contained" style={{ borderRadius: '22px' }}>
                  Salvar Variáveis
                </Button>
              </DialogActions>
            </Dialog>
          </>}
    </MainContainer>
  );
};

export default Campaigns;
